/**
 * The kiosk's language reads — the ONLY file that names both store keys this domain spans.
 *
 * It spans two slices on purpose, and they are deliberately different slices:
 *
 * | what | slice | persisted |
 * |---|---|---|
 * | the merchant's offer + default | `kioskConfigStore` (through `config/selectors`) | yes — the kiosk boots on a LAN with no internet |
 * | this customer's override | `kioskStore.language` | **no** — a restart mid-order must not resume a stranger's choice |
 *
 * `selectKioskLanguage` is the resolution of the two, and nothing else may do it. A screen that
 * reads `kioskStore.language` directly gets `null` for every customer who did not touch the
 * picker, which is almost all of them.
 *
 * Plain `(state) => value`, no memoisation, nothing read at module-eval time — the house rule
 * from `channel/selectors.ts`. `selectOfferedLanguages` is identity-stable because
 * `offeredLanguages` returns the config's own array or one frozen constant, never a fresh one.
 */
import { directionFor, type Direction } from "../../utils/i18n";
import { selectKioskConfig } from "../config/selectors";
import { isLanguageChoiceOffered, offeredLanguages, resolveLanguage } from "./languageModel";

/** This customer's choice, RAW. `null` = they did not make one. Resolve it, never render it. */
export function selectLanguageOverride(state: any): string | null {
  return state?.kioskStore?.language ?? null;
}

/** The active language, always a real tag — the customer's, else the merchant's, else `en`. */
export function selectKioskLanguage(state: any): string {
  return resolveLanguage(selectLanguageOverride(state), selectKioskConfig(state));
}

/** Which way the screen reads. The one input `DirectionRoot` needs. */
export function selectKioskDirection(state: any): Direction {
  return directionFor(selectKioskLanguage(state));
}

/** The picker's list. Identity-stable — see the header. */
export function selectOfferedLanguages(state: any): readonly string[] {
  return offeredLanguages(selectKioskConfig(state));
}

/** Is there more than one language to choose between? The feature flag is checked separately. */
export function selectLanguageChoiceOffered(state: any): boolean {
  return isLanguageChoiceOffered(selectKioskConfig(state));
}
